/* eslint-disable consistent-return */
import { definitions } from '@/types/api';
import axios from '@/axios/api';
import {
  computed,
  reactive, Ref, ref, toRaw,
} from 'vue';
import { message } from 'ant-design-vue';
import moment from 'moment';
import validate, { ValidateRuleI } from '@/lib/validate';
import { uniqueId } from 'lodash';
import { caseUnits } from '../../basicinfo/basicInfo';
import { CustomCaseClass } from '../../js/case';
// 单元计时方式
const UnitTimeModes = ref(['从0计时', '延续前一步骤']);
// 结束时间类型
const EndConditionType = ref(['时间', '点击结束按钮', '完成指定功能']);

const getUnitIdByUnitName = (name: string) => caseUnits.value.findIndex((u) => u === name);
export {
  UnitTimeModes,
  EndConditionType,
  getUnitIdByUnitName,
};

export class UnitExperiment {
  currentStep = ref(0);

  currentStepName: Ref<string>;

  formList!: Ref<definitions['CustomUnitExperiment'][]>;

  formState: definitions['CustomUnitExperiment'] = reactive({});

  quering = ref(false);

  editing = ref(false);

  conditionDeleting = ref(false);

  caseId = ref('');

  timeConditionSelected!: Ref<boolean>;

  rules: ValidateRuleI = reactive({
    unitTaskTarget: [{ required: true, message: '请输入单元任务目标' }],
    unitTaskDescription: [{ required: true, message: '请输入单元任务描述' }],
    unitExperimentObjective: [{ required: true, message: '请输入单元实验目的' }],
    unitReadyKnowledge: [{ required: true, message: '请输入单元准备知识' }],
    unitDescription: [{ required: true, message: '请输入单元准备描述' }],
    unitImg: [{ message: '请上传单元实验流程封面', required: true }],
    endInfo: [{ message: '请输入阶段结束提示', required: true }],
  })

  // 已选择的案例单元名称数组
  selectedUnits: Ref<string[]>;

  endConditionList = ref([] as definitions['CustomUnitEndCondition'][]);

  /**
   * 生成带uid的结束条件对象
   * @returns CustomUnitEndCondition
   */
  getUniqueCondition(endConditionType = 0) {
    return reactive({
      caseId: this.caseId,
      endConditionType,
      endTime: moment().set('hour', 0).set('minute', 45).toString(),
      id: '',
      uid: uniqueId(),
      unitExperimentId: '',
    } as unknown as definitions['CustomUnitEndCondition']);
  }

  resetFormState() {
    delete this.formState.unitEndConditionList;
    Object.assign(this.formState, reactive({
      id: '',
      caseId: this.caseId,
      parentId: 0,
      unitTaskTarget: '',
      unitTaskDescription: '',
      unitExperimentObjective: '',
      unitReadyKnowledge: '',
      unitDescription: '',
      unitImg: '',
      unitTimeMode: 0,
      unitEndConditionList: [this.getUniqueCondition()],
      endInfo: '', // 阶段结束提示
    } as unknown as definitions['CustomUnitExperiment']));
  }

  constructor(caseId: string, selectedUnits: Ref<string[]>) {
    this.selectedUnits = selectedUnits;
    this.currentStepName = computed(() => {
      try {
        return this.selectedUnits.value[this.currentStep.value];
      } catch (e) {
        return '';
      }
    });
    this.caseId.value = caseId;
    this.resetFormState();
    this.queryCustomUnit();

    this.timeConditionSelected = computed(() => {
      try {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        return (this as any).formState.unitEndConditionList?.findIndex((d: any) => d.endConditionType === 0) > -1;
      } catch (e) {
        return false;
      }
    });
    // this.queryCustomUnitEndCondition();
  }

  /**
   * 把endCondition 对象中的endTime 转化为 毫秒，并添加 uid 属性
   */
  parseEndConditionTime() {
    this.formList.value.forEach((v) => {
      // eslint-disable-next-line no-unused-expressions
      v.unitEndConditionList && v.unitEndConditionList?.forEach((d) => {
        // 添加uid
        if (d.endConditionType === 0) {
          (d.endTime as unknown as string) = (() => {
            const m = moment().set('hour', 0).set('minute', 0);
            return moment(m.valueOf() + (d.endTime as number) * 1000).toString();
          })();
        }
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (d as any).uid = uniqueId();
      });
    });
  }

  /**
   * 查询单元实验列表
   * @param caseId 案例id
   * @returns 单元实验数据列表
   */
  async queryCustomUnit(caseId: string = this.caseId.value) {
    try {
      this.quering.value = true;
      const { data } = await axios.queryCustomUnitExperiment(caseId);
      this.formList = ref(data.data || []);
      if (this.formList.value.length) {
        this.parseEndConditionTime();
        delete this.formState.unitEndConditionList;
        Object.assign(this.formState, reactive(this.formList.value[this.currentStep.value]));

        const CustomCase = CustomCaseClass.getInstance('unitExperiment'); // 用来同步的数据
        CustomCase.unitExperiment = this.formList.value.map((unitCase: definitions['CustomUnitExperiment']) => {
          const unitEndConditionList = unitCase.unitEndConditionList || [];
          return {
            parentId: unitCase.parentId || 0,
            unitTaskTarget: unitCase.unitTaskTarget || '', // 任务目标
            unitTaskDescription: unitCase.unitTaskDescription || '', // 任务描述
            unitExperimentObjective: unitCase.unitExperimentObjective || '', // 单元实验目的
            unitReadyKnowledge: unitCase.unitReadyKnowledge || '', // 单元准备知识
            unitDescription: unitCase.unitDescription || '', // 单元准备描述
            unitImg: unitCase.unitImg || '', // 单元实验流程
            unitTimeMode: unitCase.unitTimeMode || 0, // 单元实验计时方式
            unitEndConditionList: [...toRaw(unitEndConditionList)], // 单元实验结束条件
            endInfo: unitCase.endInfo || '', // 阶段结束提示
          };
        });
        console.log(CustomCase, 'custom', toRaw(this.formList.value), 'this.formList.value');
      }
      return data;
    } catch (e) {
      throw new Error(e);
    } finally {
      this.quering.value = false;
    }
  }

  async uploadImg(formData: FormData) {
    try {
      const { data } = await axios.uploadImg(formData);
      if (data.success) {
        this.formState.unitImg = data.data;
      } else {
        message.error(data.message);
      }
      return data;
    } catch (e) {
      return new Error(e);
    }
  }

  async queryCustomUnitEndCondition(caseId: string = this.caseId.value) {
    try {
      const { data } = await axios.queryCustomUnitEndCondition(caseId);
      this.endConditionList = ref(data.data || []);
      return data;
    } catch (e) {
      message.error(e);
    }
  }

  async delCustomUnitCondition(conditionId: string) {
    try {
      this.conditionDeleting.value = true;
      const { data } = await axios.delCustomUnitEndCondition(conditionId);
      if (!data.success) {
        message.error(data.message);
      }
      return data;
    } catch (e) {
      throw new Error(e);
    } finally {
      this.conditionDeleting.value = false;
    }
  }
}

export class UnitExperimentController {
  unitExperiment: UnitExperiment;

  emit!: Function;

  constructor(unitExperiment: UnitExperiment, emit: Function) {
    this.unitExperiment = unitExperiment;
    this.emit = emit;
  }

  handleEndConditionAdd() {
    if (this.unitExperiment.formState.unitEndConditionList?.find((d) => d.endConditionType === 0)) {
      // eslint-disable-next-line no-unused-expressions
      this.unitExperiment.formState.unitEndConditionList?.push(this.unitExperiment.getUniqueCondition(1));
    } else {
      // eslint-disable-next-line no-unused-expressions
      this.unitExperiment.formState.unitEndConditionList?.push(this.unitExperiment.getUniqueCondition());
    }
  }

  /**
   * 删除结束条件
   * @param conditionIndex 结束条件索引
   */
  delUnitEndCondition(conditionIndex: number) {
    // eslint-disable-next-line no-unused-expressions
    if (this.unitExperiment.formState.unitEndConditionList) {
      const pop = this.unitExperiment.formState.unitEndConditionList?.splice(conditionIndex, 1);
      if (pop[0].id) {
        this.unitExperiment.delCustomUnitCondition(pop[0].id);
      }
    }
  }

  /**
   * 页面保存事件
   */
  async handleSave() {
    validate(this.unitExperiment.formState, this.unitExperiment.rules);
    let timeNotSet = false;
    // eslint-disable-next-line no-unused-expressions
    this.unitExperiment.formState.unitEndConditionList?.forEach((d) => {
      if (d.endConditionType === 0) {
        if (!d.endTime) {
          // eslint-disable-next-line consistent-return
          timeNotSet = true;
        }
      }
    });
    if (timeNotSet) {
      return message.warn('请选择结束时间');
    }
    const customUnit: definitions['CustomUnitExperiment'] = JSON.parse(JSON.stringify(this.unitExperiment.formState));
    // 将moment 转化为秒
    // eslint-disable-next-line no-unused-expressions
    customUnit.unitEndConditionList?.forEach((d) => {
      if (d.endConditionType === 0) {
        const hm = moment(d.endTime).hour() * 60 * 60; // 小时转化为秒
        const mm = moment(d.endTime).minute() * 60; // 分钟转换为秒
        d.endTime = hm + mm;
      } else {
        delete d.endTime;
      }
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      delete (d as any).uid;

      if (customUnit.id) {
        d.unitExperimentId = customUnit.id;
      }
      if (!d.id) { delete d.id; } // id 如果为空字符串 后端也会保存一条id为空的数据，导致结束条件不能删除
    });
    customUnit.parentId = getUnitIdByUnitName(this.unitExperiment.currentStepName.value);

    if (!customUnit.id) delete customUnit.id;

    // 后端要求单独调用结束条件的保存接口，不可以批量保存
    // 由于结束条件需要依赖单元实验的id，所以先调用单元实验保存接口，
    // 获取单元实验的id后再并发调用结束条件保存接口，所有接口返回success后再提示用户保存成功
    let expId = '';
    this.unitExperiment.editing.value = true;
    try {
      const { data } = await axios.editCustomUnitExperiment(customUnit);
      if (data.success) {
        expId = data.data.id;
      } else {
        return message.error(data.message);
      }
    } catch (e) {
      this.unitExperiment.editing.value = false;
      return message.error('保存失败');
    }

    const reqList = (customUnit.unitEndConditionList as definitions['CustomUnitEndCondition'][]).map((d) => new Promise((resolve, reject) => {
      d.unitExperimentId = expId;
      axios.editCustomUnitEndCondition(d).then((res) => {
        if (res.data.success) {
          resolve(res);
        } else {
          reject(res.data.message);
        }
      }).catch(((e) => {
        reject(e);
      }));
    }));
    Promise.all(reqList).then(() => {
      message.success('保存成功');
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    }).catch((_) => {
      message.error('保存失败');
    }).finally(() => {
      this.unitExperiment.queryCustomUnit();
      this.unitExperiment.editing.value = false;
    });
  }

  /**
   * 点击下一步事件
   */
  handleNext() {
    if (!this.unitExperiment.formState.id) {
      message.info('请依次完成页面信息！');
    } else if (this.unitExperiment.currentStep.value === this.unitExperiment.selectedUnits.value.length - 1) {
      this.emit('next');
    } else {
      this.handleStepChange(this.unitExperiment.currentStep.value + 1);
    }
  }

  /**
   * 步骤变更事件
   * @param step 步骤index
   */
  handleStepChange(step: number) {
    if (this.unitExperiment.formList.value.length >= step || this.unitExperiment.currentStep.value > step) {
      this.unitExperiment.currentStep.value = step;
      if (this.unitExperiment.formList.value[step] !== undefined) {
        delete this.unitExperiment.formState.unitEndConditionList;
        Object.assign(this.unitExperiment.formState, this.unitExperiment.formList.value[step]);
      } else {
        this.unitExperiment.resetFormState();
      }
    } else {
      message.info('请依次完成页面信息！');
    }
  }

  /**
   * 图片上传事件
   * @param file File
   */
  handleImgUpload(file: File) {
    const formData = new FormData();
    formData.append('file', file);
    this.unitExperiment.uploadImg(formData);
  }
}
